import { TicketCheckoutStatus } from 'graphql/generated/scheme'

// A Stripe ticket checkout this browser started. Saved when the checkout opens — before the buyer pays — so a
// purchase can still be finished after the page is refreshed or closed mid-payment, when the Stripe popup is gone
// before it can tell the app the payment went through.
export interface PendingCheckout {
	/** Stripe Checkout Session id (the order's payment reference) */
	sessionId: string
	userId: string
	/** The purchaseTicket arguments the checkout page would have sent */
	orderId: string
	eventId: string
	eventName: string
	memberEventId?: string
	context?: string
	contestEntry?: boolean
	createdAt: number
}

const STORAGE_KEY = 'pending_ticket_checkouts'

// Stripe Checkout Sessions expire after 24 hours, so an older checkout can't be paid or finished any more.
const MAX_AGE_MS = 24 * 60 * 60 * 1000

// A checkout still unpaid this long after it opened was abandoned: the popup it was paid in is long gone.
export const ABANDONED_AFTER_MS = 10 * 60 * 1000

const readAll = (): PendingCheckout[] => {
	try {
		const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
		return Array.isArray(value) ? value : []
	} catch {
		return []
	}
}

const writeAll = (checkouts: PendingCheckout[]) => {
	try {
		if (checkouts.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(checkouts))
		else localStorage.removeItem(STORAGE_KEY)
	} catch {
		// Storage can be unavailable (private mode, full); the checkout then just isn't recoverable after a reload
	}
}

export const savePendingCheckout = (checkout: PendingCheckout) =>
	writeAll([...readAll().filter(x => x.sessionId !== checkout.sessionId), checkout])

export const getPendingCheckout = (sessionId: string) => readAll().find(x => x.sessionId === sessionId)

/** The user's saved checkouts that can still be finished; expired ones are dropped */
export const getPendingCheckouts = (userId: string, now = Date.now()) => {
	const all = readAll()
	const live = all.filter(x => now - x.createdAt < MAX_AGE_MS)
	if (live.length !== all.length) writeAll(live)
	return live.filter(x => x.userId === userId)
}

export const removePendingCheckout = (sessionId: string) => writeAll(readAll().filter(x => x.sessionId !== sessionId))

/**
 * localStorage.clear() for an expired sign-in, keeping the saved checkouts: a login that expires while the buyer pays
 * must not wipe the only record of a paid checkout. Each is tied to its user, so only that buyer can finish it.
 */
export const clearStorageKeepingPendingCheckouts = () => {
	const saved = readAll()
	localStorage.clear()
	writeAll(saved)
}

/**
 * - completed: the purchase went through (now or earlier)
 * - failed: the buyer paid but the purchase was refused, so the payment needs the host's attention
 * - closed: nothing to finish (never paid, abandoned unpaid, expired or cancelled)
 * - unresolved: couldn't tell yet (offline, or Stripe/the purchase still processing); try again later
 */
export type CheckoutOutcome = 'completed' | 'failed' | 'closed' | 'unresolved'

export interface SettleCheckoutDeps {
	getStatus: (sessionId: string) => Promise<TicketCheckoutStatus>
	/** Calls purchaseTicket; resolves to whether it succeeded, throws when it couldn't reach the server */
	purchase: (checkout: PendingCheckout) => Promise<boolean>
	wait: (ms: number) => Promise<void>
	now?: () => number
}

export interface SettleCheckoutOptions {
	/** Stripe just reported the payment complete: purchase straight away, like the checkout always has */
	purchaseFirst?: boolean
	/** Pauses before each further check, so their count is the number of retries */
	retryDelaysMs?: number[]
}

const settlingSessions = new Map<string, Promise<CheckoutOutcome>>()

/**
 * Drives a checkout to a final outcome. purchaseTicket is only called while the server says the checkout is
 * PAID: it claims the order before checking the payment, so calling it on a checkout that's still being paid
 * would block that payment's purchase for good. Network failures and "still processing" answers are retried.
 */
export function settleCheckout(
	checkout: PendingCheckout,
	deps: SettleCheckoutDeps,
	options: SettleCheckoutOptions = {}
): Promise<CheckoutOutcome> {
	// The checkout page and the reload recovery can both reach the same checkout (e.g. on reconnecting). The later
	// one waits for the one already running and reports its outcome, rather than "couldn't tell yet".
	const running = settlingSessions.get(checkout.sessionId)
	if (running) return running

	const settling = runSettleCheckout(checkout, deps, options).finally(() =>
		settlingSessions.delete(checkout.sessionId)
	)
	settlingSessions.set(checkout.sessionId, settling)
	return settling
}

async function runSettleCheckout(
	checkout: PendingCheckout,
	deps: SettleCheckoutDeps,
	{ purchaseFirst = false, retryDelaysMs = [1000, 2000, 4000, 8000] }: SettleCheckoutOptions
): Promise<CheckoutOutcome> {
	let purchaseRefused = false
	let lastStep: SettleStep | undefined
	for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
		if (attempt > 0) await deps.wait(retryDelaysMs[attempt - 1])
		try {
			const status =
				purchaseFirst && attempt === 0 ? TicketCheckoutStatus.Paid : await deps.getStatus(checkout.sessionId)
			lastStep = await settleStep(status, checkout, deps, purchaseRefused)
			if (lastStep === 'refused') purchaseRefused = true
			else if (lastStep !== 'pending') return lastStep
		} catch {
			// Offline or the server unreachable: retry. A purchase lost this way may have gone through, so it's no refusal
			lastStep = undefined
		}
	}
	// Still paid after every purchase was refused: refused for good (e.g. an unsigned NDA)
	return lastStep === 'refused' ? 'failed' : 'unresolved'
}

/** A final outcome, or: the purchase was refused this time / nothing to do yet, look again */
type SettleStep = CheckoutOutcome | 'refused' | 'pending'

async function settleStep(
	status: TicketCheckoutStatus,
	checkout: PendingCheckout,
	deps: SettleCheckoutDeps,
	purchaseRefused: boolean
): Promise<SettleStep> {
	switch (status) {
		case TicketCheckoutStatus.Completed:
			return 'completed'
		case TicketCheckoutStatus.Closed:
		case TicketCheckoutStatus.NotFound:
			// A refused purchase that ended the checkout voided the payment itself
			return purchaseRefused ? 'failed' : 'closed'
		case TicketCheckoutStatus.Paid:
			// PAID means no purchase has claimed the order yet, so trying again is always safe. A refusal can be a
			// passing server error, or another tab having just finished it ("already processed"): look again
			return (await deps.purchase(checkout)) ? 'completed' : 'refused'
		case TicketCheckoutStatus.Open:
			return (deps.now ?? Date.now)() - checkout.createdAt > ABANDONED_AFTER_MS ? 'closed' : 'pending'
		default:
			// Processing: Stripe or another purchase is still on it
			return 'pending'
	}
}
